// ═══════════════════════════════════════════════════════════════
// Map Engine — Hit Testing (spatial queries in world space)
// ═══════════════════════════════════════════════════════════════

import type { Camera } from "./camera";
import { HANDLE_HIT, ZONE_HEADER_H } from "./constants";
import type { MapTable, MapZone, ResizeHandle } from "./types";

/** Return the top-most table under a screen point, or null */
export function hitTable(
  tables: MapTable[],
  camera: Camera,
  sx: number,
  sy: number,
): MapTable | null {
  const p = camera.screenToWorld(sx, sy);
  for (let i = tables.length - 1; i >= 0; i--) {
    const t = tables[i];
    const cx = t.x + t.w / 2;
    const cy = t.y + t.h / 2;
    if (t.shape === "circle") {
      if ((p.x - cx) ** 2 + (p.y - cy) ** 2 <= (t.w / 2) ** 2) {
        return t;
      }
    } else {
      if (p.x >= t.x && p.x <= t.x + t.w && p.y >= t.y && p.y <= t.y + t.h) {
        return t;
      }
    }
  }
  return null;
}

/** Return the zone whose draggable area is under a screen point.
 *  For zones: header bar only. For blocks: full body. */
export function hitZoneHeader(
  zones: MapZone[],
  camera: Camera,
  sx: number,
  sy: number,
): MapZone | null {
  const p = camera.screenToWorld(sx, sy);
  for (let i = zones.length - 1; i >= 0; i--) {
    const z = zones[i];
    const bottom = z.isBlock ? z.y + z.h : z.y + ZONE_HEADER_H;
    if (p.x >= z.x && p.x <= z.x + z.w && p.y >= z.y && p.y <= bottom) {
      return z;
    }
  }
  return null;
}

/** Return the zone whose full body is under a screen point */
export function hitZoneBody(
  zones: MapZone[],
  camera: Camera,
  sx: number,
  sy: number,
): MapZone | null {
  const p = camera.screenToWorld(sx, sy);
  for (let i = zones.length - 1; i >= 0; i--) {
    const z = zones[i];
    if (p.x >= z.x && p.x <= z.x + z.w && p.y >= z.y && p.y <= z.y + z.h) {
      return z;
    }
  }
  return null;
}

/** Hit-test resize handles on the currently selected zone */
export function hitZoneHandle(
  zones: MapZone[],
  camera: Camera,
  sx: number,
  sy: number,
  selectedZoneId: string | null,
): { zone: MapZone; handle: ResizeHandle } | null {
  if (!selectedZoneId) {
    return null;
  }
  const zone = zones.find((z) => z.id === selectedZoneId);
  if (!zone) {
    return null;
  }

  const p = camera.screenToWorld(sx, sy);
  const { x, y, w, h } = zone;
  const handles: { handle: ResizeHandle; hx: number; hy: number }[] = [
    { handle: "nw", hx: x, hy: y },
    { handle: "n", hx: x + w / 2, hy: y },
    { handle: "ne", hx: x + w, hy: y },
    { handle: "e", hx: x + w, hy: y + h / 2 },
    { handle: "se", hx: x + w, hy: y + h },
    { handle: "s", hx: x + w / 2, hy: y + h },
    { handle: "sw", hx: x, hy: y + h },
    { handle: "w", hx: x, hy: y + h / 2 },
  ];

  // Scale hit radius by inverse zoom so handles stay clickable at any zoom
  const r = HANDLE_HIT / camera.scale;
  for (const { handle, hx, hy } of handles) {
    if (Math.abs(p.x - hx) <= r && Math.abs(p.y - hy) <= r) {
      return { handle, zone };
    }
  }
  return null;
}

/** Find which editable zone contains a world-space point (skips blocks) */
export function findZoneAt(zones: MapZone[], wx: number, wy: number): MapZone | null {
  for (let i = zones.length - 1; i >= 0; i--) {
    const z = zones[i];
    if (z.isBlock) {
      continue;
    }
    if (wx >= z.x && wx <= z.x + z.w && wy >= z.y && wy <= z.y + z.h) {
      return z;
    }
  }
  return null;
}
